import React, { Component } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'

import { Button } from '@wf-common/atoms'
import MappedTagSection from './mapped-tag-section'
import { availableSectionsPropType } from './props/available-sections-prop-type'
import { itemSourcesPropType } from './props/item-sources-prop-type'
import { sectionMappingsPropType } from './props/section-mappings-prop-type'
import SelectionDialog from './selection-dialog'

import styles from './tag-section-editor.module.css'

const propTypes = {
    addButtonClassName: PropTypes.string,
    addButtonText: PropTypes.string.isRequired,
    availableSections: availableSectionsPropType.isRequired,
    availableSectionsDescription: PropTypes.string,
    cancelButtonClassName: PropTypes.string,
    className: PropTypes.string,
    dialogAddButtonText: PropTypes.string.isRequired,
    dialogCancelButtonText: PropTypes.string.isRequired,
    dialogClassName: PropTypes.string,
    dialogTitle: PropTypes.string.isRequired,
    extendedRightButtonClassName: PropTypes.string,
    formatTagLabel: PropTypes.func,
    itemSources: itemSourcesPropType.isRequired,
    itemSourcesDescription: PropTypes.string,
    name: PropTypes.string.isRequired,
    onAdd: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    sectionDropdownClassName: PropTypes.string,
    sectionMappings: sectionMappingsPropType,
    tagSectionClassName: PropTypes.string
}

/**
 * A higher-order widget composed of other t3-widgets
 * Used to place items into specific "buckets" (sections)
 * The items can be of different types - for example: cars, airplanes, boats
 * The visualization of current section-item-mappings is achieved using an array of <TagSection>s
 * Each "bucket" is a <TagSection>, each item within is a <Tag>
 * The widget can also trigger a removal of an item from a section by clicking on the CloseIcon of the <Tag>
 * A <Button> is displayed at the bottom to trigger the process of adding items to a specific section
 * To add items to a section a dialog is displayed that shows items <TagDropdown>s for each source
 *
 * For an example of use see "extended rights management" in the user's edit mode:
 * https://confluence.tomtomgroup.com/display/TTB/Organisms#Organisms-Edit
 *
 * @example
 * <TagSectionEditor {...{onAdd, onRemove}} name='disasters:' addButtonText='Add a disaster'
 *    dialogTitle='Disaster Machine' dialogAddButtonText='Add' dialogCancelButtonText='Cancel'
 *    itemSources={getItemSources()}
 *    availableSections={[ {id: 2, name: 'burned'}, {id: 3, name: 'drowned'} ]}
 *    sectionMappings={[
 *      {
 *        id: 1, items: [{ itemId: 2, sourceId: 'boat' }]
 *      }
 *    ]}/>
 *
 * function getItemSources () {
 *   return [{
 *       id: 'boat',
 *       placeholder: 'Boats',
 *       icon: <BoatIcon />,
 *       items: [{
 *           id: 1,
 *           name: 'boat-1'
 *       }, {
 *           id: 2,
 *           name: 'boat-2'
 *       }]
 *   }]
 *
 * Props:
 * For more detailed props documentation please refer to props.js
 *
 * @param {Object} props - The current component props
 * @param {sectionMappingsPropType} [props.sectionMappings] - which items are currently in which category
 * @param {itemSourcesPropType} props.itemSources - sources that provide an array of available items
 * @param {availableSectionsPropType} props.availableSections - which "buckets" are available to put items in
 * @param {number|string} props.name - name of the editor, used as left title for <TagSection />s
 * @param {Function} props.onAdd - fired when items are added to a section
 * @param {Function} props.onRemove - fired when items are removed from a section
 * @param {string} props.addButtonText - text to put on the button used to initiate the dialog
 * @param {string} props.dialogTitle - title of the dialog that is used to add items to a section
 * @param {string} props.dialogAddButtonText - text on the 'Add'-button in the dialog
 * @param {string} props.dialogCancelButtonText - text on the 'Cancel'-button in the dialog
 * @param {string} [props.itemSourcesDescription] - explanation text for item sources dropdowns (dialog)
 * @param {string} [props.availableSectionsDescription] - explanation text for sections dropdown (dialog)
 * @param {string} [props.className] - css class to add to the styling of the element
 * @param {string} [props.dialogClassName] - css class to add to the styling of the dialog
 * @param {Function} [props.formatTagLabel] - a function which is used to create the tag label
 * @param {string} [props.tagSectionClassName]  - css class to add to the styling of the <TagSection>s
 * @param {string} [props.sectionDropdownClassName] - css class to add to the sections dropdown in the dialog
 * @param {string} [props.extendedRightButtonClassName] - css class to add to the "Add extended right" button
 * @param {string} [props.addButtonClassName]   - css class to add to the add-button in the dialog
 * @param {string} [props.cancelButtonClassName]- css class to add to the cancel-button in the dialog
 */
class TagSectionEditor extends Component {
    constructor(props) {
        super(props)
        this.changeDialogVisibility = this.changeDialogVisibility.bind(this)
        this.showDialog = this.showDialog.bind(this)
        this.hideDialog = this.hideDialog.bind(this)
        this.onAdd = this.onAdd.bind(this)
        this.state = {
            showDialog: false
        }
    }

    render() {
        const { className } = this.props
        return (
            <div {...{ className }}>
                {this.renderSections()}
                {this.renderAddButton()}
                {this.renderDialog()}
            </div>
        )
    }

    renderAddButton() {
        const { addButtonText, extendedRightButtonClassName } = this.props
        return (
            <Button
                className={cx(
                    styles['add-button'],
                    extendedRightButtonClassName,
                    't3sel-tag-section-editor-add-button'
                )}
                onClick={this.showDialog}
            >
                {addButtonText}
            </Button>
        )
    }

    renderSections() {
        const { availableSections, formatTagLabel, itemSources, name, onRemove, sectionMappings, tagSectionClassName } =
            this.props

        return sectionMappings.map((sectionMapping, idx) => (
            <MappedTagSection
                key={idx}
                {...{
                    availableSections,
                    formatTagLabel,
                    itemSources,
                    name,
                    onRemove,
                    sectionMapping,
                    tagSectionClassName
                }}
            />
        ))
    }

    renderDialog() {
        const {
            addButtonClassName,
            availableSections,
            availableSectionsDescription,
            cancelButtonClassName,
            dialogAddButtonText,
            dialogCancelButtonText,
            dialogClassName,
            dialogTitle,
            itemSources,
            itemSourcesDescription,
            sectionDropdownClassName
        } = this.props

        if (!this.state.showDialog) {
            return null
        }

        return (
            <SelectionDialog
                addButtonText={dialogAddButtonText}
                cancelButtonText={dialogCancelButtonText}
                className={dialogClassName}
                onAdd={this.onAdd.bind(this)}
                onCancel={this.hideDialog}
                title={dialogTitle}
                {...{
                    addButtonClassName,
                    availableSections,
                    availableSectionsDescription,
                    cancelButtonClassName,
                    itemSources,
                    itemSourcesDescription,
                    sectionDropdownClassName
                }}
            />
        )
    }

    onAdd(sectionId, items) {
        this.props.onAdd(sectionId, items)
        this.hideDialog()
    }

    showDialog() {
        this.changeDialogVisibility(true)
    }

    hideDialog() {
        this.changeDialogVisibility(false)
    }

    changeDialogVisibility(show) {
        this.setState({
            showDialog: show
        })
    }
}

TagSectionEditor.propTypes = propTypes

export default TagSectionEditor
